import { Operator, UrlFilters } from '../hooks/useUrlFilters';

export type TestType = {
  testName: string[];
  testSubType?: string[];
  title: string;
  key: string;
  filterOperator?: Operator;
  testSubTypeOperator?: Operator;
  testCategory?: string;
};

export const buildTestTypeFilters = (testType: TestType): UrlFilters => {
  if (testType.testCategory) {
    return [
      {
        property: 'testCategory',
        operator: 'equals',
        value: [testType.testCategory],
      },
    ];
  }

  const operator = testType.filterOperator ?? 'equals';
  const testSubTypeOperator = testType.testSubTypeOperator ?? 'equals';

  const filters: UrlFilters = [
    {
      property: 'testName',
      operator,
      value: testType.testName,
    },
  ];

  if (testType.testSubType) {
    filters.push({
      property: 'testSubType',
      operator: testSubTypeOperator,
      value: testType.testSubType,
    });
  }

  return filters;
};

export const ELEMENTARY_TEST_NAMES = [
  'event_freshness_anomalies',
  'freshness_anomalies',
  'table_anomalies',
  'volume_anomalies',
  'json_schema',
  'schema_changes',
  'schema_changes_from_baseline',
  'all_columns_anomalies',
  'column_anomalies',
  'dimension_anomalies',
  'volume',
  'source_freshness',
] as const;

export const TEST_TYPES: TestType[] = [
  {
    testName: [
      'event_freshness_anomalies',
      'freshness_anomalies',
      'table_anomalies',
      'source_freshness',
    ],
    testSubType: ['freshness', 'generic', 'event_freshness', 'automated'],
    title: 'Freshness',
    key: 'freshness',
    testCategory: 'freshness',
  },
  {
    testName: ['volume_anomalies', 'table_anomalies', 'volume'],
    testSubType: ['row_count', 'generic', 'automated'],
    title: 'Volume',
    key: 'volume',
    testCategory: 'volume',
  },
  {
    testName: ['json_schema', 'schema_changes', 'schema_changes_from_baseline'],
    title: 'Schema changes',
    key: 'schemaChanges',
    testCategory: 'schemaChanges',
  },
  {
    testName: [...ELEMENTARY_TEST_NAMES],
    title: 'Data tests',
    key: 'dbtTests',
    filterOperator: 'notEquals',
    testCategory: 'dbtTests',
  },
  {
    testName: [
      'all_columns_anomalies',
      'column_anomalies',
      'dimension_anomalies',
    ],
    title: 'Anomalies',
    key: 'anomalies',
    testCategory: 'anomalies',
  },
];

// The first matching test type wins, so a test belongs to exactly one category,
// unlike the testName/testSubType filters which overlap between categories.
export const getTestType = ({
  testName,
  testSubType,
}: {
  testName: string;
  testSubType?: string;
}): TestType | undefined =>
  TEST_TYPES.find((testType) => {
    const isInTestNames = testType.testName.includes(testName);
    const testNameMatch =
      testType.filterOperator === 'notEquals' ? !isInTestNames : isInTestNames;

    const testSubTypeMatch = testType.testSubType
      ? testType.testSubType.includes(testSubType ?? '')
      : true;

    return testNameMatch && testSubTypeMatch;
  });
